import { usePaneContext } from "@/components/pane/paneContext.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { MarketRatio } from "@/components/shared/marketRatio.tsx";
import { Ratio } from "@/components/shared/ratio.tsx";
import { TableRowRenderer } from "@/components/shared/tableRowRenderer.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx";
import { AddFavoriteAlt } from "@/components/widgets/controlPanel/favorites/addFavoriteAlt.tsx";
import { FolderName } from "@/components/widgets/controlPanel/favorites/folderName.tsx";
import { MoveFavorite } from "@/components/widgets/controlPanel/favorites/moveFavorite.tsx";
import { RemoveFavorite } from "@/components/widgets/controlPanel/favorites/removeFavorite.tsx";
import { RemoveFolder } from "@/components/widgets/controlPanel/favorites/removeFolder.tsx";
import { ToggleExpand } from "@/components/widgets/controlPanel/favorites/toggleExpand.tsx";
import { IFavoriteFolder } from "@/domain/interfaces/dashboard.interface.ts";
import {
  IParam,
  MarketPowerItem,
  RetailPowerItem,
} from "@/domain/interfaces/general.interface.ts";
import { IActivityDetector } from "@/hooks/useActivityDetector";
import {
  IAggregatedConstructor,
  IAggregatedData,
} from "@/hooks/useAggregatedConstructor.ts";
import { PriceData } from "@/hooks/useBinancePrices.ts";
import { ICryptoDrift } from "@/hooks/useCryptoDrift";
import { useGrid } from "@/hooks/useGrid.ts";
import { IMarketRatio } from "@/hooks/useMarketRatioAll.ts";
import { useDashboard } from "@/lib/store.ts";
import { cn, prepareFormula } from "@/lib/utils.ts";
import {
  ColumnDef,
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import Formula from "fparser";
import { memo, useCallback, useMemo, useRef, useState, useEffect } from "react";
import { HiArrowDown, HiArrowUp } from "react-icons/hi2";
import { FillAll } from "./fillAll";
import { SelectColumns } from "./selectColumns";
import { useFavorites } from "@/hooks/useFavorites.ts";
import { time } from "framer-motion";

type IFolderRow = IFavoriteFolder & {
  isFolder: boolean;
};
type ISymbolRow = IAggregatedConstructor & {
  name: string;
  folderId: string | null;
  isPinned: boolean;
  folderColor?: string;
  currentPrice: PriceData;
  marketRatio: IMarketRatio;
};
type IFavoritesRow = Partial<IFolderRow & ISymbolRow>;

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
};

function RemovePinned({ symbolName }: { symbolName: string }) {
  const { updatePinned } = useDashboard((state) => ({
    updatePinned: state.updatePinned,
  }));

  const handleRemovePinned = useCallback(() => {
    updatePinned((state) => state.filter((s) => s !== symbolName));
  }, [symbolName, updatePinned]);
  return (
    <Button
      size="xs"
      variant="ghost"
      onClick={handleRemovePinned}
      className="w-4 h-4"
    >
      <i className="fa-regular fa-trash" />
    </Button>
  );
}

const FolderRow = memo(function FolderRow({
  row,
  size,
  isSelected,
  ...props
}: {
  row: Row<IFolderRow>;
  size: number;
  isSelected: boolean;
}) {
  const { color, id } = row.original;

  return (
    <TableRow
      className={cn(
        "h-[17px] border-l-4 bg-neutral-900 border-b-neutral-700",
        isSelected && "bg-neutral-800",
      )}
      style={{
        borderLeftColor: color,
      }}
      {...props}
    >
      {row.getVisibleCells().map((cell, index) => {
        if (index === 0) {
          // First cell contains folder name and controls
          return (
            <TableCell
              key={cell.id}
              style={{
                width: cell.column.getSize(),
                maxWidth: cell.column.columnDef.maxSize,
              }}
            >
              <div className="flex items-center gap-1">
                <ToggleExpand folderId={id} />
                <FolderName folderId={id} />
              </div>
            </TableCell>
          );
        }
        // Empty cells for other columns to maintain width
        return (
          <TableCell
            key={cell.id}
            style={{
              width: cell.column.getSize(),
              maxWidth: cell.column.columnDef.maxSize,
            }}
          />
        );
      })}
      <TableCell className="w-[60px] p-0">
        <div className="flex items-center justify-end w-full gap-1 pr-1">
          <FillAll folderId={id} />
          <AddFavoriteAlt folderId={id} />
          <RemoveFolder folderId={id} />
        </div>
      </TableCell>
    </TableRow>
  );
});

const SymbolRow = memo(function SymbolRow({
  row,
  size,
  isSelected,
  ...props
}: {
  row: Row<ISymbolRow>;
  size: number;
  isSelected: boolean;
}) {
  const { folderColor, name: symbol } = row.original;
  const { setSymbolForColor, colorsToSymbolMap } = useDashboard((state) => ({
    setSymbolForColor: state.setSymbolForColor,
    colorsToSymbolMap: state.colorsToSymbolMap,
  }));
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const color = getNodeConfig().color;
  const nodeSymbol = colorsToSymbolMap[color];

  useEffect(() => {
    if (isSelected) {
      setSymbolForColor(color, symbol);
    }
  }, [isSelected, color, symbol, setSymbolForColor]);

  return (
    <TableRow
      className={cn(
        "h-[17px] border-l-4 cursor-pointer text-right border-b-neutral-700",
        symbol === nodeSymbol && "bg-[mediumblue] active",
        isSelected ? "bg-neutral-800" : "bg-transparent",
      )}
      style={{
        borderLeftColor: folderColor,
      }}
      onClick={() => {
        copyToClipboard(symbol);
        setSymbolForColor(color, symbol);
      }}
      {...props}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          style={{
            width: cell.column.getSize(),
            maxWidth: cell.column.columnDef.maxSize,
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
      <TableCell className="w-[60px] p-0">
        <div className="flex items-center justify-end w-full gap-1 pr-1">
          <MoveFavorite
            sourceFolderId={row.original.folderId}
            favoriteName={row.original.name}
          />
          {row.original.folderId ? (
            <RemoveFavorite
              folderId={row.original.folderId}
              favoriteName={row.original.name}
            />
          ) : (
            <RemovePinned symbolName={row.original.name} />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
});

const sortFolderContent = (
  a: IFavoritesRow,
  b: IFavoritesRow,
  columnId: string,
  isAsc: boolean,
) => {
  if (!a || !b) {
    return 0;
  }

  // Get the values to compare based on the column ID
  const getValue = (item: IFavoritesRow) => {
    // Handle symbol column
    if (columnId === "symbol") {
      return item.name;
    }

    if (columnId.startsWith("marketRatio.")) {
      const period = columnId.split(".")[1];
      return (item as any)?.marketRatio?.data?.[period]?.value;
    }

    if (columnId.includes("spotFuturesSpread")) {
      if (columnId.includes("funding")) {
        return (item as any)?.funding;
      } else {
        return (item as any)?.spotFutureSpread?.value;
      }
    }

    if (columnId.startsWith("manipulationMonitor.")) {
      console.log(item);
      if (columnId.includes("corrV")) {
        return (item as any)?.manipulationMonitor?.corrV;
      } else {
        return (item as any)?.manipulationMonitor?.corr;
      }
    }

    // Handle currentPrice special case
    if (columnId.startsWith("currentPrice.")) {
      const field = columnId.split(".")[1];
      return (item as any)?.currentPrice?.[field];
    }

    // Handle nested properties with time periods
    const timePeriods = ["15m", "30m", "1h", "4h", "1d", "1w", "1M"];
    if (timePeriods.includes(columnId.split(".")[1])) {
      const [mainKey, period] = columnId.split(".");
      return (item as any)?.[mainKey]?.[period];
    }

    // Handle nested objects with value property
    if ((item as any)?.[columnId]?.value !== undefined) {
      return (item as any)[columnId].value;
    }

    // Handle direct properties
    return (item as any)?.[columnId];
  };

  const aVal = getValue(a);
  const bVal = getValue(b);

  if (aVal === undefined && bVal === undefined) {
    return 0;
  }
  if (aVal === undefined) {
    return 1;
  }
  if (bVal === undefined) {
    return -1;
  }

  const multiplier = isAsc ? 1 : -1;
  return aVal < bVal ? -1 * multiplier : aVal > bVal ? 1 * multiplier : 0;
};

const HighlightedCell = memo(function HighlightedCell({
  value,
}: {
  value: number;
}) {
  return (
    <TableRowRenderer>
      <HighlightedValueChange value={value} />
    </TableRowRenderer>
  );
});

const RatioCell = memo(function RatioCell({ value }: { value: number }) {
  if (!value) {
    return null;
  }
  return (
    <div className="flex justify-end">
      <Ratio
        leftValue={value}
        rightValue={100 - value}
        height={17}
        width={60}
      />
    </div>
  );
});

const MarketRatioCell = memo(function MarketRatioCell({ data }: { data: any }) {
  if (!data) {
    return null;
  }
  return <MarketRatio data={data} height={17} width={60} />;
});

export function FavoritesInner() {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [sorting, setSorting] = useState<
    (ColumnSort & { id: string | undefined })[]
  >([]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { favorites, favoritesSettings } = useDashboard((state) => ({
    favorites: state.favorites,
    favoritesSettings: state.favoritesSettings,
  }));

  const nodeId = usePaneContext();
  const { node } = useGrid(nodeId);

  const data = useFavorites();

  const sortedData = useMemo(() => {
    // First, group data by folders
    const folderGroups: { [key: string]: IFavoritesRow[] } = {
      pinned: [],
    };

    // Initialize folder groups
    favorites.forEach((folder) => {
      folderGroups[folder.id] = [];
    });

    // Distribute data into groups
    data.forEach((item) => {
      if (item.isPinned) {
        folderGroups.pinned.push(item);
      } else if (item.isFolder) {
        // Skip folders as they'll be added later
        return;
      } else if (item.folderId) {
        folderGroups[item.folderId].push(item);
      }
    });

    // Add a direct volume property to each item
    Object.keys(folderGroups).forEach((groupKey) => {
      folderGroups[groupKey].forEach((item: any) => {
        item.volume = item.volumeQuote?.["1d"] ?? 0;
      });
    });

    // Sort each group if sorting is active
    if (sorting.length > 0) {
      const [{ id: columnId, desc }] = sorting;

      Object.keys(folderGroups).forEach((groupKey) => {
        folderGroups[groupKey].sort((a, b) =>
          sortFolderContent(a, b, columnId, !desc),
        );
      });
    }

    // Reconstruct the flat array maintaining folder order
    const result: IFavoritesRow[] = [];

    // Add pinned items first
    result.push(...folderGroups.pinned);

    // Add folders and their sorted content
    favorites.forEach((folder) => {
      result.push({
        ...folder,
        isFolder: true,
        name: folder.name,
      });

      if (folder.expanded) {
        result.push(...folderGroups[folder.id]);
      }
    });

    return result;
  }, [data, sorting, favorites]);

  const columns = useMemo(() => {
    const cols: ColumnDef<IFavoritesRow>[] = [];

    cols.push({
      id: "name",
      accessorKey: "name",
      header: "Symbol",
      size: 100,
      cell: (info) => {
        const value = info.getValue() as string;
        return <div className="font-semibold text-left">{value}</div>;
      },
      enableSorting: true,
    });

    favoritesSettings.activeColumns.forEach((col) => {
      const timeFrame = col.split(".")[1];
      const timeFrameTitle = timeFrame ? `${timeFrame} ` : "";

      switch (true) {
        case col.endsWith("price"):
          cols.push({
            id: "price",
            accessorFn: (originalRow) => originalRow.currentPrice?.price,
            header: "Price",
            size: 100,
            cell: (info) => {
              return <HighlightedCell value={info.getValue() as number} />;
            },
            enableSorting: false,
          });
          break;
        case col.endsWith("volume"):
          cols.push({
            id: "volume",
            accessorFn: (row) => (row as any).volume,
            header: "24H Vol$",
            size: 80,
            maxSize: 80,
            minSize: 80,
            cell: (info) => {
              const val = info.getValue() as number;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={val}
                    highlightPosNeg={false}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;
        case col.includes("priceChg."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}Pr %Chg`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("openInterestChg."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}OI %Chg`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("volumeChg."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}V %Chg`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("volumeQuote."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}V Quote`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("adtv."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}ADTV`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("williamsPrcR."):
          cols.push({
            id: col,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}WP %Chg`,
            size: 60,
            cell: (info) => (
              <HighlightedCell value={info.getValue() as number} />
            ),
            enableSorting: true,
          });
          break;

        case col.includes("manipulationMonitor."):
          cols.push({
            id: col,
            enableSorting: true,
            accessorFn: (row) => {
              if (timeFrame === "Price Activity") {
                return (row as any).manipulationMonitor?.corr;
              } else {
                return (row as any).manipulationMonitor?.corrV;
              }
            },
            header: timeFrame === "Price Activity" ? "MM P" : "MM V",
            size: 60,
            cell: (info) => {
              const value = ((1 - (info.getValue() as number)) * 100) / 2;
              if (info.getValue() === 0) {
                return <div className="text-[#d6d6d6]">-</div>;
              }
              let color = "";
              switch (true) {
                case value > 99:
                  color = "text-[#f91a36]";
                  break;
                case value > 75:
                  color = "text-[#b2182c]";
                  break;
                case value > 50:
                  color = "text-[#600b17]";
                  break;
                case value > 25:
                  color = "text-[#1a5824]";
                  break;
                case value > 1:
                  color = "text-[#3ea54b]";
                  break;
                default:
                  color = "text-[#d6d6d6]";
                  break;
              }
              return <div className={color}>{value.toFixed(1)}%</div>;
            },
          });
          break;

        case col.includes("spotFuturesSpread."): {
          const isFunding = timeFrame === "funding";
          cols.push({
            id: col,
            enableSorting: true,
            accessorFn: (row) =>
              isFunding
                ? (row as any).funding
                : (row as any).spotFutureSpread?.value,
            header: isFunding ? "Funding %" : "Spread",
            size: 60,
            cell: (info) => {
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={
                      isFunding
                        ? (info.getValue() as number) * 100
                        : (info.getValue() as number)
                    }
                    decimal={isFunding ? 3 : 2}
                  />
                </TableRowRenderer>
              );
            },
          });
          break;
        }

        case col.includes("buySellRatioChg."):
          cols.push({
            id: col,
            enableSorting: true,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: `${timeFrameTitle}BvS %Chg`,
            size: 60,
            cell: (info) => <RatioCell value={info.getValue() as number} />,
          });
          break;

        case col.includes("marketRatio."):
          cols.push({
            id: col,
            enableSorting: true,
            accessorFn: (row) => (row as any)[col.split(".")[0]]?.[timeFrame],
            header: timeFrameTitle.replace("1", "") + "Market R%",
            size: 80,
            cell: (info) => {
              // console.log(info.row.original);
              const marketRatioTimeFrames = (info.row.original as any)[
                col.split(".")[0]
              ]?.data;

              const marketRatio =
                marketRatioTimeFrames?.[
                  timeFrame as keyof typeof marketRatioTimeFrames
                ];

              if (!marketRatio) {
                return null;
              }
              return <MarketRatioCell data={marketRatio} />;
            },
          });
          break;

        case col === "funding":
          cols.push({
            id: col,
            accessorKey: "funding",
            header: "Funding %",
            size: 60,
            cell: (info) => {
              const val = (info.getValue() as number) * 100;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange value={val} decimal={4} />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;

        case col === "openInterest":
          cols.push({
            id: col,
            accessorKey: "openInterest",
            header: "OI $",
            size: 60,
            cell: (info) => {
              const price = Number(info.row.original.currentPrice?.price);
              const val = Number(info.getValue()) * price;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={val}
                    highlightPosNeg={false}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;

        case col === "cryptoDrift":
          cols.push({
            id: col,
            accessorKey: "cryptoDrift",
            header: "Crypto Drift",
            size: 60,
            cell: (info) => {
              const row = info.row.original.cryptoDrift as ICryptoDrift;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={row?.value}
                    highlightPosNeg={true}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;

        case col === "activityDetector":
          cols.push({
            id: col,
            accessorKey: "activityDetector",
            header: "Activity Detector",
            size: 60,
            cell: (info) => {
              const row = info.row.original
                .activityDetector as IActivityDetector;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={row?.value}
                    highlightPosNeg={true}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;

        case col === "marketPower":
          cols.push({
            id: col,
            accessorKey: "marketPower",
            header: "Market Power",
            size: 60,
            cell: (info) => {
              const row = info.row.original.marketPower as MarketPowerItem;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={row?.value}
                    highlightPosNeg={true}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;

        case col === "retailPower":
          cols.push({
            id: col,
            accessorKey: "retailPower",
            header: "Retail Power",
            size: 60,
            cell: (info) => {
              const row = info.row.original.retailPower as RetailPowerItem;
              return (
                <TableRowRenderer>
                  <HighlightedValueChange
                    value={row?.value}
                    highlightPosNeg={true}
                    shorten={true}
                  />
                </TableRowRenderer>
              );
            },
            enableSorting: true,
          });
          break;
      }
    });

    return cols;
  }, [favoritesSettings.activeColumns]);

  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    // enableSortingRemoval: false,
    manualSorting: true,
    sortDescFirst: false,
    debugTable: false,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 17,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!rows.length) {
        return;
      }

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          setSelectedRowIndex((prev) => Math.max(0, prev - 1));
          break;
        case "ArrowDown":
          event.preventDefault();
          setSelectedRowIndex((prev) => Math.min(rows.length - 1, prev + 1));

          break;
        case "Enter":
          // Handle row selection confirmation if needed
          break;
      }
    },
    [rows.length],
  );

  useEffect(() => {
    if (!node) {
      return;
    }

    if (selectedRowIndex >= 0 && virtualRows.length > 0) {
      const selectedVirtualRow = virtualRows.find(
        (row) => row.index === selectedRowIndex,
      );
      if (selectedVirtualRow) {
        const rowElement = tableContainerRef.current?.querySelector(
          `[data-index="${selectedRowIndex}"]`,
        );
        rowElement?.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedRowIndex, virtualRows, node]);

  if (!node) {
    return null;
  }

  const height = node.getRect().height - 122;

  return (
    <div
      ref={tableContainerRef}
      className="w-full overflow-auto"
      style={{
        height: `${height}px`,
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (selectedRowIndex === -1 && rows.length > 0) {
          setSelectedRowIndex(0);
        }
      }}
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-[17px] text-xs">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize(),
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: `flex items-center ${header.column.id === "name" ? "justify-start" : "justify-end"}`,
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <HiArrowUp className="ml-2 h-4 w-4" />,
                        desc: <HiArrowDown className="ml-2 h-4 w-4" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </TableHead>
              ))}
              <TableHead className="w-[60px] p-0">
                <div className="flex justify-end pr-1">
                  <SelectColumns />
                </div>
              </TableHead>
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {paddingTop > 0 && (
            <TableRow>
              <td
                colSpan={rows[0].getVisibleCells().length}
                style={{ height: `${paddingTop}px` }}
              />
            </TableRow>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<IFavoritesRow>;
            if (row.original.isFolder) {
              return (
                <FolderRow
                  key={virtualRow.index}
                  row={row as Row<IFolderRow>}
                  size={virtualRow.size}
                  isSelected={virtualRow.index === selectedRowIndex}
                  data-index={virtualRow.index}
                />
              );
            } else {
              return (
                <SymbolRow
                  key={virtualRow.index}
                  row={row as Row<ISymbolRow>}
                  size={virtualRow.size}
                  isSelected={virtualRow.index === selectedRowIndex}
                  data-index={virtualRow.index}
                />
              );
            }
          })}
          {paddingBottom > 0 && (
            <TableRow>
              <td
                colSpan={rows[0].getVisibleCells().length}
                style={{ height: `${paddingBottom}px` }}
              />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
